import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type PropsWithChildren,
} from "react";
import { useQuery } from "@tanstack/react-query";

import type {
    DocumentSetStructure,
    DocumentSubscriptionData,
} from "@hsl/core/documents/types";
import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";

import {
    type SubscribableAssetCreate,
    type UserDetails,
} from "../../../../../openapi";
import { useSubscriptionContext } from "../../../Root";
import {
    createSubscriptionsHelper,
    updateSubscriptionsHelper,
} from "./helpers";
import { fetchDocStructure } from "./services";
import type { SelectDocumentContext, SelectionDocument } from "./types";

export const SelectDocumentsContext = createContext<
    SelectDocumentContext | undefined
>(undefined);

type Props = {
    type: SubscribableAssetCreate["subscriptionType"];
};

export const useDocumentStructure = (share_classes?: number[]) => {
    const DOCUMENT_STRUCTURE_ID = 104;
    const {
        config: { audienceId: audience, languageId: language },
    } = useSubscriptionContext();
    return useQuery({
        queryKey: [
            "docs",
            share_classes,
            language,
            audience,
            DOCUMENT_STRUCTURE_ID,
        ],
        queryFn: () =>
            fetchDocStructure(DOCUMENT_STRUCTURE_ID, {
                language,
                audience,
                share_classes,
            }),
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchInterval: false,
    });
};

export const getAvailableDocuments = (
    docsData?: Array<
        DocumentSetStructure & {
            subscription_data: DocumentSubscriptionData;
        }
    >,
    type?: "key" | "all",
): SelectionDocument[] => {
    return docsData
        ? docsData
              .filter(
                  (d) => type !== "key" || d.settings?.litAccordion === "Key",
              )
              .map((d) => ({
                  setId: d.set_id,
                  name: d.display_name,
                  subscription_data: d.subscription_data,
                  scSelector: Boolean(d.settings?.shareClassSelector),
              }))
        : [];
};

export const SelectDocumentsContextProvider = ({
    children,
    type,
}: PropsWithChildren<Props>) => {
    const { dropdownEvent } = useYardActionEvents();
    const stepDataSelectFunds = useFCSubscriptionStore(
        (state) => state.flowSteps["select-funds"],
    );
    const stepData = useFCSubscriptionStore(
        (state) => state.flowSteps["select-documents"],
    );
    const setStepData = useFCSubscriptionStore(
        (state) => state.setFlowStepData,
    );
    const share_classes = stepDataSelectFunds.data?.funds.flatMap(
        (fund) => fund.data.share_classes,
    );
    const { data, status } = useDocumentStructure(share_classes);
    const [applyToAll, setApplyToAll] = useState<boolean>(true);

    const updateSelection: SelectDocumentContext["updateSelection"] =
        useCallback(
            (args) => {
                setStepData(
                    "select-documents",
                    updateSubscriptionsHelper(
                        stepData.data?.subscriptions ?? [],
                        stepData.data?.dayOfWeek ?? "MONDAY",
                        stepDataSelectFunds.data?.funds ?? [],
                        args,
                    ),
                );
            },
            [
                setStepData,
                stepData.data?.dayOfWeek,
                stepData.data?.subscriptions,
                stepDataSelectFunds.data?.funds,
            ],
        );

    const updateDayOfWeek = useCallback(
        (dayOfWeek: UserDetails["dayOfWeek"]) => {
            const { complete, data } = stepData;
            setStepData("select-documents", {
                data: {
                    ...data,
                    dayOfWeek,
                },
                complete,
            });
            dropdownEvent({
                name: "Edit weekly day",
                value: `${dayOfWeek}`,
                position: "Timing of roundup emails",
            });
        },
        [setStepData, stepData, dropdownEvent],
    );

    const createSubscriptions = useCallback(
        (
            doc: SelectionDocument,
            restrict?: { funds?: string[]; shareClasses?: string[] },
        ) => {
            return createSubscriptionsHelper(
                doc,
                restrict,
                type,
                stepDataSelectFunds.data?.funds,
            );
        },
        [stepDataSelectFunds.data?.funds, type],
    );

    const contextValue = useMemo<SelectDocumentContext>(
        () => ({
            status,
            availableFunds: stepDataSelectFunds.data?.funds ?? [],
            availableShareClasses: stepDataSelectFunds.data?.shareClasses ?? [],
            allDocuments: getAvailableDocuments(data),
            keyDocuments: getAvailableDocuments(data, "key"),
            get applyToAllFunds(): boolean {
                return applyToAll;
            },
            set applyToAllFunds(v: boolean) {
                setApplyToAll(v);
            },
            subscriptionType: type,
            stepData,
            updateSelection,
            updateDayOfWeek,
            createSubscriptions,
        }),
        [
            status,
            stepDataSelectFunds.data,
            data,
            stepData,
            applyToAll,
            setApplyToAll,
            updateSelection,
            updateDayOfWeek,
            createSubscriptions,
            type,
        ],
    );
    return (
        <SelectDocumentsContext.Provider value={contextValue}>
            {children}
        </SelectDocumentsContext.Provider>
    );
};

export function useSelectDocumentContext() {
    const context = useContext(SelectDocumentsContext);
    if (!context) throw new Error("No SelectDocumentContextProvider");
    return context;
}
